/**
 * 시간 포맷팅 관련 유틸 함수 모듈
 * @module core/time/format
 */
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);
/**
 * ms를 'YYYY:MM:DD HH:mm:ss' 포맷으로 변환
 */
export const changeDurationMsToDateTimeFormat = (ms: number | null) => {
  if (ms === null) {
    return '';
  }
  return dayjs.duration(ms).format('YYYY:MM:DD HH:mm:ss');
};

const LEADING_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

/**
 * 타임존 정보가 없는 벽시계(wall-clock) datetime 문자열에서 날짜 부분(`YYYY-MM-DD`)만 추출
 *
 * 서버가 tz-suffix 없이 에코하는 date-only 성격의 값(EOL/EOS `"2026-07-24T23:59:59"` 등)에
 * 타임존 변환을 적용하면 음수 오프셋 타임존에서 날짜가 하루 밀리므로, 변환 없이 문자열에서
 * 날짜부만 잘라낸다.
 *
 * @param dateTime 벽시계 datetime 문자열 (`YYYY-MM-DDTHH:mm:ss`, `YYYY-MM-DD HH:mm:ss`, `YYYY-MM-DD`)
 * @returns `YYYY-MM-DD` 문자열. 입력이 nullish이거나 날짜로 시작하지 않으면 `''`
 * @example
 * extractDateOnly('2026-07-24T23:59:59'); // '2026-07-24'
 * extractDateOnly(undefined); // ''
 * @remarks UTC 저장값을 사용자 타임존으로 변환해 보여줘야 하는 값에는 사용 금지
 * (그 경우 `utcZeroTimeToStandardTime` 사용). 날짜 유효성(2026-13-99 등)은 검증하지 않는다.
 */
export const extractDateOnly = (dateTime?: string | null): string =>
  dateTime?.match(LEADING_DATE_REGEX)?.[0] ?? '';

/**
 * interval에 따라 시간 포맷팅
 */
export const formatTimeByInterval = (time: number | string, interval: string) => {
  const datetime = dayjs(time);
  if (interval === 'I1d') {
    return datetime.format('YYYY-MM-DD 00:00:00');
  }
  if (interval === 'I1h') {
    return datetime.format('YYYY-MM-DD HH:00:00');
  }
  if (interval === 'I10m') {
    const minutes = datetime.minute();
    const roundedMinutes = minutes - (minutes % 10);
    return datetime.minute(roundedMinutes).second(0).format('YYYY-MM-DD HH:mm:00');
  }
  if (interval === 'I1m') {
    return datetime.second(0).format('YYYY-MM-DD HH:mm:00');
  }
  if (interval === 'I5s') {
    const seconds = datetime.second();
    const roundedSeconds = seconds - (seconds % 5);
    return datetime.second(roundedSeconds).format('YYYY-MM-DD HH:mm:ss');
  }
  return datetime.format('YYYY-MM-DD HH:mm:ss');
};
